import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';

import { getActionAriaLabelSync } from '@/utils/ariaLabels';
import { hasAddedLines } from '@/utils/checkout';

import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { Loader } from '@/components/Loader';
import { Price } from '@/components/Price';
import { ProductDetail } from '@/components/ProductDetail';
import type { SelectOption } from '@/components/Select';
import { TaxedPrice } from '@/components/TaxedPrice';
import { Typography } from '@/components/Typography';
import type {
  OrderAppCtaType,
  OrderAppProductVariantsBusinessDetailsQuery,
} from '@/graphql/orderApp/api';
import {
  type TaxedMoneyFragment,
  useProductVariantDetailImageQuery,
} from '@/graphql/saleor/api';
import { useAddToBagFromModal } from '@/hooks/useAddToBagFromModal';
import type { AllSelectedProducts } from '@/hooks/useAllSelectedProductsState';
import { useAllSelectedProductsState } from '@/hooks/useAllSelectedProductsState';
import type {
  StrapAddToBagBuckle,
  StrapAddToBagTool,
} from '@/hooks/useBucklesAndTools';
import { useCheckout } from '@/hooks/useCheckout';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useIsMdDown } from '@/hooks/useMedia';
import { useWristSize } from '@/hooks/useWristSize';
import type { StrapHit } from '@/integrations/algolia/types';
import { DETAIL_PAGE_IMAGE_SIZE, NEXT_PUBLIC_IMAGES_FORMAT } from '@/lib/const';
import { EVENT_NAME, popinTitle } from '@/lib/gtm/const';
import { isToolInShoppingBag } from '@/lib/product';
import { getTaxMessageDetail } from '@/lib/regions';
import { getStrapMaterial } from '@/lib/straps';
import type { Maybe, SetState } from '@/lib/types';
import { commonMessages } from '@/messages/common';
import { useCommonModalProviderContext } from '@/providers/CommonModalProvider';
import { useRegionProvider } from '@/providers/RegionProvider';
import { px2rem } from '@/theme';
import { defined } from '@/views/ShoppingBagView/components/ShoppingBagStrapDrawer/components/ShoppingBagStrapDetails/messages';

import { CTAForStrapPack } from '../CtaForStrapPack/CTAForStrapPack';
import type { SizeState } from '../ProductDetailSection';
import { SizesModal } from '../Sizes/components';
import { BucklesSection, NiceToHaveSection } from './components';
import { StrapImageWrapper } from './components/StrapImageWrapper';
import { translateStrapSize } from './helpers';

import { ReactComponent as InfoBadge } from '@/assets/icons/info-badge.svg';

import * as S from './AddToBagDrawer.styles';
import * as M from './messages';

type AddToBagDrawerType = 'strap' | 'strapPack';
type strapSizeData = { id: string; size: string; sku: string };
export interface AddToBagDrawerProps {
  allProduct?: AllSelectedProducts | null; //all selected products
  buckles: StrapAddToBagBuckle[] | null;
  businessData: Maybe<OrderAppProductVariantsBusinessDetailsQuery>;
  cta: OrderAppCtaType | undefined;
  isOpen: boolean;
  niceToHaveProducts?: StrapAddToBagTool[] | null;
  onAddedToBag?: () => void;
  onPackItemClick?: (value: AllSelectedProducts) => void;
  setIsOpen: SetState<boolean>;
  sizeState: SizeState;
  strap?: Partial<StrapHit>;
  strapId: string;
  strapPrice: TaxedMoneyFragment;
  strapSku: Maybe<string>;
  title?: string;
  type?: AddToBagDrawerType;
}

export const AddToBagDrawer = ({
  allProduct,
  buckles,
  businessData,
  cta,
  isOpen,
  niceToHaveProducts,
  onAddedToBag,
  onPackItemClick,
  setIsOpen,
  sizeState,
  strap,
  strapId,
  strapPrice,
  strapSku: sku,
  title,
  type = 'strap',
}: AddToBagDrawerProps) => {
  const [isSizesModalOpen, setIsSizesModalOpen] = useState(false);
  const isMobileView = useIsMdDown();
  const [selectedSizesItem, setSelectedSizesItem] = useState<
    Maybe<SelectOption>
  >(sizeState?.sizesSelectedItem);
  const { locale: regionLocale, query, unit } = useRegionProvider();
  const [strapSku, setStrapSku] = useState(sku);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setModalRoot(document.getElementById('modal-root'));
  }, []);

  const initialSelectedProduct: AllSelectedProducts = {
    products: [
      {
        id: strapId,
        productTypeSlug: 'strap',
        price: strapPrice,
        sku: strapSku,
      },
    ],
    totalPrice: strapPrice,
  };
  const { allSelectedProducts, setAllSelectedProducts } =
    useAllSelectedProductsState(initialSelectedProduct);
  const { data, loading, refetch } = useProductVariantDetailImageQuery({
    variables: {
      thumbnailSize: DETAIL_PAGE_IMAGE_SIZE,
      imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
      sku: strapSku as string,
      channel: query.channel,
    },
  });

  useEffect(() => {
    if (strapSku)
      refetch({
        sku: strapSku as string,
      });
  }, [strapSku]);

  const { addToBagFromModal } = useAddToBagFromModal(allSelectedProducts);
  const {
    snapshot: { checkout, isProcessing },
  } = useCheckout();
  const { formatMessage } = useIntl();

  const { setContactUsModalStatus } = useCommonModalProviderContext();

  useEffect(() => {
    // only need replace strap data
    setAllSelectedProducts((curr) => {
      return {
        products: [
          ...curr.products.filter((item) => item.productTypeSlug !== 'strap'),
          {
            id: (selectedSizesItem?.data as { id: string; sku: string })?.id,
            productTypeSlug: 'strap',
            price: strapPrice,
            sku: (selectedSizesItem?.data as { id: string; sku: string })?.sku,
          },
        ],
        totalPrice: curr.totalPrice,
      } satisfies AllSelectedProducts;
    });
  }, [selectedSizesItem]);

  const closeModal = () => {
    setAllSelectedProducts({
      products: [
        {
          id: strapId,
          productTypeSlug: 'strap',
          price: strapPrice,
          sku: strapSku,
        },
      ],
      totalPrice: strapPrice,
    });
    setSelectedSizesItem(null);
    setIsOpen(false);
  };

  const handleModalClose = () => {
    if (isOpen) {
      sendEvent({
        event: EVENT_NAME.POPIN_CLOSE,
        popin_title: popinTitle.addStrap,
      });

      closeModal();
    }
  };

  useEffect(() => {
    if (allProduct) setAllSelectedProducts(allProduct);
  }, [allProduct]);

  const containerRef = useRef<HTMLDivElement>(null);

  const handleAddToCart = async () => {
    if (cta === 'ORDER' || cta === 'PRE_ORDER') {
      if (!isAddToBagAvailable) {
        setErrorMessage(formatMessage(commonMessages.selectAvailableStrapSize));
        if (containerRef.current) {
          containerRef.current.scrollTop = 0;
        }
        return;
      }
      const addedLines = await addToBagFromModal();
      if (hasAddedLines(addedLines)) {
        onAddedToBag?.();
      }
      closeModal();
    } else {
      setContactUsModalStatus(true, strapSku);
    }
  };
  const onSaveBuckles = () => {
    if (cta === 'ORDER' || cta === 'PRE_ORDER') {
      if (allSelectedProducts) onPackItemClick?.(allSelectedProducts);
      closeModal();
    } else {
      setContactUsModalStatus(true, strapSku);
    }
  };
  const buckleProducts = buckles?.filter((item) => {
    const productCTA = businessData?.productVariants?.find(
      (jtem) => jtem.sku === item.sku,
    );
    return productCTA?.variantCTA === 'ORDER';
  });

  const isStrapPack = type === 'strapPack';

  const { sendEvent } = useGoogleAnalytics();
  const strapMaterial = getStrapMaterial(strap?.material_en ?? 'unknown');
  const wristInterval = useWristSize(
    (selectedSizesItem?.data as strapSizeData)?.size,
    unit,
    strapMaterial,
  );
  useEffect(() => {
    if (isOpen) {
      setSelectedSizesItem(sizeState?.sizesSelectedItem);
      sendEvent({
        event: EVENT_NAME.POPIN_VIEW,
        popin_title: popinTitle.addStrap,
      });
    }
  }, [isOpen]);

  const taxMessage = getTaxMessageDetail(query.channel);

  const strapThumbnailUrl = data?.productVariant?.product.thumbnail?.url;

  // Needed because the CTA from PLP set the standard strap even if not available
  const isAddToBagAvailable = !!(
    selectedSizesItem?.value &&
    sizeState?.selectItems?.find(
      (item) => item.value === selectedSizesItem?.value,
    )
  );

  if (!isOpen || !modalRoot) {
    return null;
  }

  return createPortal(
    <S.DrawerOverlay className='modal-open' onClick={handleModalClose}>
      <S.Wrapper>
        <S.HeaderWrapper>
          <S.Header>
            <S.HeaderText>
              {title ?? formatMessage(M.defined.addAStrap)}
            </S.HeaderText>
            <S.CloseButton
              aria-label={
                getActionAriaLabelSync('close_modal', regionLocale.code) ||
                'Close'
              }
              onClick={handleModalClose}
              type='button'
            >
              <Icon
                aria-hidden='true'
                name='close'
                size='m'
                variant='regular'
              />
            </S.CloseButton>
          </S.Header>
        </S.HeaderWrapper>
        <S.Body ref={containerRef}>
          {errorMessage && <S.ErrorMessage>{errorMessage}</S.ErrorMessage>}
          <S.StrapWrapper>
            <S.ImageWrapper className='image-wrapper'>
              <StrapImageWrapper
                alt={strapSku as string}
                imageUrl={strapThumbnailUrl}
                loading={loading}
              ></StrapImageWrapper>
            </S.ImageWrapper>
            <Flex
              alignItems='flex-start'
              flexDirection='column'
              justifyContent='center'
            >
              <Typography
                color='secondary'
                fontFamily='italianPlate'
                scale='p1'
                textTransform='uppercase'
              >
                {strap?.name?.[regionLocale?.code?.toUpperCase() || 'EN_GB'] ||
                  strap?.name?.['EN_GB'] ||
                  strap?.name?.['EN_US']}
              </Typography>
              <Typography
                color='secondaryGrey'
                fontFamily='italianPlate'
                scale='p2'
                style={{ marginTop: px2rem(2) }}
              >
                {(selectedSizesItem?.data as { id: string; sku: string })?.sku}
              </Typography>
              {!strap?.integrated_buckle && !isStrapPack && (
                <S.InfoBadgeWrapper>
                  <InfoBadge height={16} width={16} />
                  <Typography
                    color='info'
                    fontFamily='italianPlate'
                    scale='p2'
                    style={{ marginTop: px2rem(2) }}
                  >
                    {formatMessage(M.defined.buckleNotIncluded)}
                  </Typography>
                </S.InfoBadgeWrapper>
              )}
              {!isStrapPack && (
                <Typography as='div' color='secondary' scale='p2'>
                  <Price
                    color='secondary'
                    price={{
                      amount: strap?.price as number,
                      currency: strap?.currency as string,
                    }}
                    size='inherited'
                  />
                </Typography>
              )}
            </Flex>
          </S.StrapWrapper>
          <S.BucklesBox>
            {sizeState?.selectItems?.length !== 0 && (
              <S.OtherSizesBox>
                {sizeState?.isLoadingOtherSize ? (
                  <Loader></Loader>
                ) : (
                  <>
                    <S.AvailableSizeTitle>
                      {formatMessage(defined.availableSize)}
                    </S.AvailableSizeTitle>
                    <S.SizesContainer
                      $selectItemsLength={sizeState?.selectItems?.length}
                    >
                      {sizeState?.selectItems?.map((r) => (
                        <S.SizesTitle
                          key={r.value}
                          isMobile={isMobileView}
                          isSelected={r.value === selectedSizesItem?.value}
                          onClick={() => {
                            setStrapSku(
                              (r.data as { id: string; sku: string })?.sku,
                            );
                            setSelectedSizesItem?.(r);
                            setErrorMessage(null);
                          }}
                        >
                          <Typography>
                            {translateStrapSize(r.label, formatMessage)}
                          </Typography>
                        </S.SizesTitle>
                      ))}
                      <S.Ruler
                        data-testid='ruler'
                        fill='#C4C4C4'
                        onClick={() => setIsSizesModalOpen(true)}
                      />
                      <SizesModal
                        isOpen={isSizesModalOpen}
                        setIsOpen={setIsSizesModalOpen}
                      />
                    </S.SizesContainer>
                  </>
                )}
                <S.SizeInfoText>
                  {M.MeasuringText(wristInterval.min, wristInterval.max, unit)}
                </S.SizeInfoText>
              </S.OtherSizesBox>
            )}
            <BucklesSection
              allProduct={allProduct}
              buckles={buckleProducts ?? []}
              setAllSelectedProducts={setAllSelectedProducts}
            />
            {buckleProducts &&
              niceToHaveProducts &&
              !isToolInShoppingBag(checkout) && (
                <NiceToHaveSection
                  allProduct={allProduct}
                  niceToHaveProducts={niceToHaveProducts}
                  setAllSelectedProducts={setAllSelectedProducts}
                />
              )}
          </S.BucklesBox>
          {!isStrapPack && (
            <S.BottomTextWrapper>
              <ul>
                <li>
                  <Typography>
                    {formatMessage(commonMessages.priceVatNoticePart1, {
                      taxMessage: taxMessage?.defaultMessage,
                    })}
                  </Typography>
                </li>
                <li>
                  <Typography>
                    {formatMessage(commonMessages.priceVatNoticePart2)}
                  </Typography>
                </li>
              </ul>
            </S.BottomTextWrapper>
          )}
        </S.Body>
        <S.Footer>
          {!isStrapPack && (
            <S.TotalWrapper>
              <Typography
                fontFamily='italianPlate'
                fontWeight='bold'
                mr={2}
                scale='h4'
                textTransform='uppercase'
              >
                {formatMessage(commonMessages.total)}
              </Typography>
              <Typography as='div' fontWeight='bold' scale='h4'>
                <TaxedPrice
                  channel={checkout?.channel?.slug}
                  displayZero
                  price={allSelectedProducts.totalPrice}
                  size='inherited'
                />
              </Typography>
            </S.TotalWrapper>
          )}
          <S.AddToBagButton>
            {isStrapPack ? (
              <CTAForStrapPack cta={cta} onClick={onSaveBuckles} />
            ) : (
              <ProductDetail.CTA
                cta={cta}
                isProcessing={isProcessing}
                loading={false}
                onClick={handleAddToCart}
              />
            )}
          </S.AddToBagButton>
        </S.Footer>
      </S.Wrapper>
    </S.DrawerOverlay>,
    modalRoot,
  );
};
